import type { HintKind } from './hints.js';
import { isPathComplete, isWin } from './isWin.js';
import {
  type ColorId,
  type GameState,
  type Level,
  type MoveResult,
  type Pos,
  WALL,
  cloneState,
  posEqual,
  rebuildGrid,
} from './types.js';
import { getEndpointAt, validateExtendPath } from './validateMove.js';

/** Number of "Путь"/reveal hints granted per level; nudge hints are unlimited. */
export const HINT_REVEALS_PER_LEVEL = 3;

export function createStateFromLevel(level: Level): GameState {
  const endpoints = new Map<ColorId, [Pos, Pos]>();
  for (const pair of level.pairs) {
    endpoints.set(pair.color, [pair.a, pair.b]);
  }

  const paths = new Map<ColorId, Pos[]>();
  for (const pair of level.pairs) {
    paths.set(pair.color, []);
  }

  const walls = level.walls ?? [];

  return {
    size: level.size,
    endpoints,
    paths,
    grid: rebuildGrid(level.size, paths, walls),
    walls,
    hintRevealsRemaining: HINT_REVEALS_PER_LEVEL,
    status: 'playing',
  };
}

export interface GameEngineOptions {
  requireFullGrid?: boolean;
}

export class GameEngine {
  private state: GameState;
  private undoStack: GameState[] = [];
  private options: GameEngineOptions;
  /** How many times `undo()` has actually fired this attempt — used for star scoring, not rewindable itself. */
  private undoCount = 0;
  /** How many times the player started dragging from an already-fully-connected
   *  pair's endpoint (silently wiping the finished path in `startPath`) —
   *  tracked separately from `undoCount` for star scoring, not rewindable itself. */
  private redrawCount = 0;

  constructor(level: Level, options: GameEngineOptions = {}) {
    this.state = createStateFromLevel(level);
    this.options = { requireFullGrid: true, ...options };
  }

  getState(): GameState {
    return this.state;
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  getUndoCount(): number {
    return this.undoCount;
  }

  getRedrawCount(): number {
    return this.redrawCount;
  }

  startPath(color: ColorId, pos: Pos): MoveResult {
    if (this.state.status === 'won') {
      return { ok: false, reason: 'game-won' };
    }

    const pair = this.state.endpoints.get(color);
    if (!pair) return { ok: false, reason: 'unknown-color' };

    if (!posEqual(pair[0], pos) && !posEqual(pair[1], pos)) {
      return { ok: false, reason: 'not-endpoint' };
    }

    this.pushUndo();

    const existing = this.state.paths.get(color) ?? [];
    if (existing.length > 0) {
      if (isPathComplete(existing, pair)) {
        this.redrawCount += 1;
      }
      this.clearPathInternal(color);
    }

    this.state.paths.set(color, [[...pos] as Pos]);
    this.state.grid = rebuildGrid(this.state.size, this.state.paths, this.state.walls);

    return { ok: true };
  }

  extendPath(color: ColorId, pos: Pos): MoveResult {
    if (this.state.status === 'won') {
      return { ok: false, reason: 'game-won' };
    }

    const path = this.state.paths.get(color) ?? [];
    if (path.length === 0) {
      return this.startPath(color, pos);
    }

    if (!validateExtendPath(this.state, color, pos)) {
      return { ok: false, reason: 'invalid-move' };
    }

    if (path.length >= 2 && posEqual(path[path.length - 2], pos)) {
      this.pushUndo();
      path.pop();
      this.state.grid = rebuildGrid(this.state.size, this.state.paths, this.state.walls);
      return { ok: true };
    }

    this.pushUndo();
    path.push([...pos] as Pos);
    this.state.grid = rebuildGrid(this.state.size, this.state.paths, this.state.walls);

    if (isWin(this.state, this.options.requireFullGrid)) {
      this.state.status = 'won';
      return { ok: true, won: true };
    }

    return { ok: true };
  }

  clearPath(color: ColorId): MoveResult {
    if (this.state.status === 'won') {
      return { ok: false, reason: 'game-won' };
    }

    const path = this.state.paths.get(color);
    if (!path || path.length === 0) {
      return { ok: false, reason: 'empty-path' };
    }

    this.pushUndo();
    this.clearPathInternal(color);
    return { ok: true };
  }

  reset(): void {
    this.undoStack = [];
    this.undoCount = 0;
    this.redrawCount = 0;
    const endpoints = this.state.endpoints;
    const size = this.state.size;
    const paths = new Map<ColorId, Pos[]>();
    for (const color of endpoints.keys()) {
      paths.set(color, []);
    }
    this.state = {
      size,
      endpoints,
      paths,
      grid: rebuildGrid(size, paths, this.state.walls),
      walls: this.state.walls,
      hintRevealsRemaining: HINT_REVEALS_PER_LEVEL,
      status: 'playing',
    };
  }

  undo(): MoveResult {
    if (this.undoStack.length === 0) {
      return { ok: false, reason: 'nothing-to-undo' };
    }

    this.state = this.undoStack.pop()!;
    this.undoCount += 1;
    return { ok: true };
  }

  /** Nudge hints are free and unlimited; reveal hints consume the per-level allowance. */
  useHint(kind: HintKind): void {
    if (kind === 'reveal' && this.state.hintRevealsRemaining > 0) {
      this.state.hintRevealsRemaining -= 1;
    }
  }

  getColorAtEndpoint(pos: Pos): ColorId | null {
    for (const [color, [a, b]] of this.state.endpoints) {
      if (posEqual(a, pos) || posEqual(b, pos)) return color;
    }
    return null;
  }

  getColorAtCell(pos: Pos): ColorId | null {
    const [x, y] = pos;
    if (y < 0 || y >= this.state.size || x < 0 || x >= this.state.size) {
      return null;
    }
    const cell = this.state.grid[y][x];
    return cell === WALL ? null : cell;
  }

  private pushUndo(): void {
    this.undoStack.push(cloneState(this.state));
    if (this.state.status === 'won') {
      this.state.status = 'playing';
    }
  }

  private clearPathInternal(color: ColorId): void {
    this.state.paths.set(color, []);
    this.state.grid = rebuildGrid(this.state.size, this.state.paths, this.state.walls);
  }
}

export { getEndpointAt };
